// THE CANVAS WAITS FOR THE SESSION BRIDGE'S VERDICT BEFORE IT BOOTS.
//
// Measured by relman (ms after navigation, a clean profile — the ORDER, not
// the outcome): the canvas host creates the session-bridge frame, then routes
// to /login ~300 ms later, BEFORE the peer's symbols-session-ready /
// -response arrive —
//   canvas.dev            frame   753  /login 1,047  response 2,414
//   canvas.staging        frame 1,967  /login 2,275  response 4,460
//   edit.symbols.app      frame   559  /login   936  response 2,759
// The requester (the SDK's hydrateAuthFromIframeBridge, started by getSDK)
// writes the bridged tokens into this host's storage when the answer
// arrives; nothing waited for it, so the shared boot's auth gate read an empty
// storage and decided "signed out". A signed-in user with no canvas-local
// session on that host saw sign-in on a cold load; a reload worked because
// the late answer had been stored.
//
// So the canvas boot waits for that verdict first — and ONLY for it:
//   • only on a protected path — a guest / public path never waits
//     (isGuestAllowedPath, the one public-path list the boot gate reads);
//   • only as long as the verdict takes: the requester resolves on the
//     response, at once on `ok:false`, and false after its own 8 s (cold ask); this gate
//     adds a ceiling above that (CEILING_MS) so the boot can never hang;
//   • it decides NOTHING: the shared boot then reads storage exactly as
//     before, so a timeout or `ok:false` (no token written) still fails
//     closed to /login. A fresh local session resolves the requester at once
//     (it never creates a frame), so a signed-in reload does not wait.
// The token itself is handled only by the requester, after its origin check
// (`ev.origin !== origin` → ignored) — this gate never reads a message.
//
// Same pattern as the shell guard (workspace 99389ba6a): wait while gated
// with no verdict, bounded, then decide.
// Above the cold bridge ask's own 8 s (shared sdkClient: staging answered
// at 4,202 / 4,460 ms, past the old 4 s — 2026-10-11).
export const CEILING_MS = 9000

// Resolves { waited, verdict } — `verdict` is what the requester answered
// (true = tokens written) or 'ceiling' when the ceiling ended the wait first.
export const awaitBridgeVerdict = async ({
  pathname,
  isGuestPath,
  startHydration,
  ceilingMs = CEILING_MS
} = {}) => {
  const path = typeof pathname === 'string' && pathname ? pathname : '/'
  if (typeof isGuestPath === 'function' && isGuestPath(path)) {
    return { waited: false, verdict: 'guest' }
  }
  if (typeof startHydration !== 'function') return { waited: false, verdict: 'none' }
  let hydration
  try {
    hydration = startHydration()
  } catch (_) {
    return { waited: false, verdict: 'none' }
  }
  if (!hydration || typeof hydration.then !== 'function') {
    return { waited: false, verdict: 'none' }
  }
  let timer = null
  const ceiling = new Promise((resolve) => {
    timer = setTimeout(() => resolve('ceiling'), ceilingMs)
  })
  try {
    const verdict = await Promise.race([
      hydration.then(
        (v) => !!v,
        () => false
      ),
      ceiling
    ])
    return { waited: true, verdict }
  } finally {
    if (timer !== null) clearTimeout(timer)
  }
}
